import React, { useState, useRef, useEffect } from 'react'

export interface GanttStage {
  id: number
  name: string
  description: string
  order: number
  duration_days: number
  start_offset_days: number
  status: string
  status_display: string
  planned_start_date?: string
  actual_start_date?: string
  actual_end_date?: string
}

interface GanttChartProps {
  stages: GanttStage[]
  onStageUpdate: (stageId: number, updates: Partial<GanttStage>) => void
  editable?: boolean
  rfpReleaseDate?: Date
}

const GanttChart: React.FC<GanttChartProps> = ({ 
  stages, 
  onStageUpdate, 
  editable = true,
  rfpReleaseDate = new Date()
}) => {
  const [draggedStage, setDraggedStage] = useState<number | null>(null)
  const [dragMode, setDragMode] = useState<'move' | 'resize' | null>(null)
  const chartRef = useRef<HTMLDivElement>(null)

  // Calculate cumulative start days for each stage
  const calculateStageDates = () => {
    let cumulativeDay = 0
    return stages.map(stage => {
      const startDay = cumulativeDay + stage.start_offset_days
      cumulativeDay = startDay + stage.duration_days
      return {
        ...stage,
        startDay,
        endDay: cumulativeDay
      }
    })
  }

  const stagesWithDates = calculateStageDates()
  const totalDays = Math.max(...stagesWithDates.map(s => s.endDay), 90)
  const dayWidth = 8 // pixels per day

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500'
      case 'in_progress':
        return 'bg-blue-500'
      case 'delayed':
        return 'bg-red-500'
      default:
        return 'bg-slate-400'
    }
  }

  const formatDate = (daysFromStart: number) => {
    const date = new Date(rfpReleaseDate)
    date.setDate(date.getDate() + daysFromStart)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const handleBarMouseDown = (stageId: number, mode: 'move' | 'resize', e: React.MouseEvent) => {
    if (!editable) return
    e.preventDefault()
    setDraggedStage(stageId)
    setDragMode(mode)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggedStage || !dragMode || !editable) return

    const chartRect = chartRef.current?.getBoundingClientRect()
    if (!chartRect) return

    const deltaX = e.movementX
    const deltaDays = Math.round(deltaX / dayWidth)

    if (deltaDays === 0) return

    const stage = stages.find(s => s.id === draggedStage)
    if (!stage) return

    if (dragMode === 'move') {
      // Update start_offset_days
      const newOffset = Math.max(-stage.duration_days + 1, stage.start_offset_days + deltaDays)
      onStageUpdate(draggedStage, { start_offset_days: newOffset })
    } else if (dragMode === 'resize') {
      // Update duration_days
      const newDuration = Math.max(1, stage.duration_days + deltaDays)
      onStageUpdate(draggedStage, { duration_days: newDuration })
    }
  }

  const handleMouseUp = () => {
    setDraggedStage(null)
    setDragMode(null)
  }

  useEffect(() => {
    if (draggedStage) {
      document.addEventListener('mouseup', handleMouseUp as any)
      return () => document.removeEventListener('mouseup', handleMouseUp as any)
    }
  }, [draggedStage])

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
          Selection Timeline
        </h3>
        <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
          <span>Total: {totalDays} days</span>
          <span>Start: {formatDate(0)}</span>
          <span>End: {formatDate(totalDays)}</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-400 dark:bg-slate-600 rounded"></div>
          <span className="text-slate-600 dark:text-slate-400">Not Started</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 rounded"></div>
          <span className="text-slate-600 dark:text-slate-400">In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span className="text-slate-600 dark:text-slate-400">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-slate-600 dark:text-slate-400">Delayed</span>
        </div>
      </div>

      {/* Chart */}
      <div 
        ref={chartRef}
        className="relative overflow-x-auto"
        onMouseMove={handleMouseMove}
        style={{ cursor: draggedStage ? 'grabbing' : 'default' }}
      >
        {/* Timeline header */}
        <div className="flex items-center h-8 mb-2 sticky top-0 bg-white dark:bg-slate-800 z-10">
          <div className="w-48 flex-shrink-0"></div>
          <div className="relative flex" style={{ width: `${totalDays * dayWidth}px` }}>
            {[...Array(Math.ceil(totalDays / 7))].map((_, weekIndex) => {
              return (
                <div 
                  key={weekIndex} 
                  className="text-xs text-slate-500 dark:text-slate-400 border-l border-slate-200 dark:border-slate-700 pl-1"
                  style={{ width: `${7 * dayWidth}px` }}
                >
                  Week {weekIndex + 1}
                </div>
              )
            })}
          </div>
        </div>

        {/* Stage rows */}
        {stagesWithDates.map((stage) => (
          <div 
            key={stage.id} 
            className="flex items-center h-16 border-b border-slate-100 dark:border-slate-700 last:border-0"
          >
            {/* Stage name column */}
            <div className="w-48 flex-shrink-0 pr-4">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                {stage.name}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {stage.duration_days} days
                {stage.start_offset_days !== 0 && ` • ${stage.start_offset_days > 0 ? '+' : ''}${stage.start_offset_days}d offset`}
              </div>
            </div>

            {/* Timeline column */}
            <div className="relative flex" style={{ width: `${totalDays * dayWidth}px` }}>
              {/* Week separators */}
              {[...Array(Math.ceil(totalDays / 7))].map((_, weekIndex) => (
                <div 
                  key={weekIndex}
                  className="absolute h-full border-l border-slate-100 dark:border-slate-700"
                  style={{ left: `${weekIndex * 7 * dayWidth}px` }}
                />
              ))}

              {/* Stage bar */}
              <div
                className={`absolute h-8 rounded-lg ${getStatusColor(stage.status)} 
                  ${editable ? 'cursor-move hover:opacity-80' : ''} 
                  ${draggedStage === stage.id ? 'ring-2 ring-blue-500 opacity-80' : ''}
                  transition-all flex items-center justify-between px-2 text-white text-xs font-medium shadow-sm`}
                style={{
                  left: `${stage.startDay * dayWidth}px`,
                  width: `${stage.duration_days * dayWidth}px`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  minWidth: '40px'
                }}
                onMouseDown={(e) => handleBarMouseDown(stage.id, 'move', e)}
                title={`${stage.name}\nDuration: ${stage.duration_days} days\nOffset: ${stage.start_offset_days} days\nStatus: ${stage.status_display}`}
              >
                <span className="truncate flex-1 text-center">
                  {stage.duration_days >= 3 ? stage.name : ''}
                </span>
                {editable && (
                  <div
                    className="w-2 h-full bg-white bg-opacity-30 cursor-ew-resize hover:bg-opacity-50"
                    onMouseDown={(e) => {
                      e.stopPropagation()
                      handleBarMouseDown(stage.id, 'resize', e)
                    }}
                  />
                )}
              </div>

              {/* Start date label */}
              <div
                className="absolute text-xs text-slate-500 dark:text-slate-400"
                style={{
                  left: `${stage.startDay * dayWidth}px`,
                  bottom: '-20px'
                }}
              >
                {formatDate(stage.startDay)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editable && (
        <div className="mt-4 text-xs text-slate-500 dark:text-slate-500 italic">
          Tip: Drag bars to adjust timing. Drag the right edge to change duration.
        </div>
      )}
    </div>
  )
}

export default GanttChart

